import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Modal } from '@zeeve-platform/modal-component';
import { Button } from '@zeeve-platform/button-component';
import { Inputbox } from '@zeeve-platform/inputbox-component';
import {
  getEndpointDetailsData,
  updateEndpointName,
  updateSecurity,
  getSubsComQuotaData,
} from '../../../services/apisService';
import Loading from '../../../components/common/loader';
import Urls from '../../../utils/util';
import Snippet from './snippet';

export type EndointDetailsTypes = {
  id: string;
  serviceId: string;
  endpointName: string;
  userId: string;
  workspaceId: string;
  apiKey: string;
  networkType: string;
  securityEnabled?: boolean;
  jwtEnabled?: boolean;
  jwtPublicName?: string;
  jwtPublicKey?: string;
  plan: string;
  addOns: string[];
};

type Props = {
  setEndpointName: React.Dispatch<React.SetStateAction<string>>;
};

function Basics({ setEndpointName }: Props) {
  const { id } = useParams<{ id: string }>();
  const [endpointData, setEndpointData] = useState({} as EndointDetailsTypes);
  const [tempEndpointData, setTempEndpointData] = useState(
    {} as EndointDetailsTypes,
  );
  const [isDisabled, setIsDisabled] = useState<{
    epName: boolean;
    epSecurity: boolean;
  }>({ epName: true, epSecurity: true });
  const [modalShow, setModalShow] = useState<boolean>(false);
  const [loaderShow, setLoaderShow] = useState<boolean>(true);
  const [securityErr, setSecurityErr] = useState<string>('');
  const [endpointId, setEndpointId] = useState<string>('');
  const [modalTitle, setModalTitle] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [btnText, setBtnText] = useState<string>('');
  const [isCopiedTxt, setIsCopiedTxt] = useState<boolean>(false);
  const [defaultName, setDefaultName] = useState<string>('');
  const [isClose, setIsClose] = useState<boolean>(false);
  const [consumption, setConsumption] = useState<number>(0);
  const [quota, setQuota] = useState<number>(0);
  const [validationMsg, setValidationMsg] = useState<string>('');

  const saveName = async () => {
    if (validateName()) {
      setLoaderShow(true);
      setModalTitle('Status');
      setBtnText('Ok');
      setIsDisabled((prev) => ({ ...prev, epName: true }));
      const payload = {
        id: endpointId,
        name: endpointData.endpointName,
      };
      const response: any = await updateEndpointName(payload, 'name');
      setLoaderShow(false);
      setModalShow(true);
      setIsClose(false);
      if (response) {
        if (response && response.success) {
          setMessage(response.data.message);
        } else {
          setMessage(response.message);
        }
      } else {
        setMessage('Server Error...');
      }
    }
  };

  const validateName = () => {
    if (
      !('endpointName' in endpointData)
      || endpointData.endpointName.length < 5
      || endpointData.endpointName.length > 50
    ) {
      setValidationMsg('Invalid endpoint name.');
      highlight('endpoint-name', false);
      return false;
    }
    if (
      !endpointData.endpointName.match(
        /^[A-Za-z]([A-Za-z0-9\s-]){3,48}[A-Za-z0-9]$/,
      )
    ) {
      setValidationMsg('Invalid endpoint name.');
      highlight('endpoint-name', false);
      return false;
    }
    if (defaultName === endpointData.endpointName) {
      setValidationMsg('');
      removeHighlight('endpoint-name', true);
      return false;
    }
    setValidationMsg('');
    removeHighlight('endpoint-name', true);
    return true;
  };

  const validateSecurityDetails = (): boolean => {
    if (
      'securityEnabled' in endpointData
      && endpointData.securityEnabled === true
    ) {
      if (
        !('jwtEnabled' in endpointData)
        || endpointData.jwtEnabled === false
      ) {
        highlight('require-jwt', false);
        setSecurityErr('Please select require jwt field.');
        return false;
      }
      if (
        !('jwtPublicName' in endpointData)
        || endpointData.jwtPublicName === ''
      ) {
        setSecurityErr('JWT public key name should not be null');
        highlight('jwt-key-name', false);
        return false;
      }
      if (
        (endpointData.jwtPublicName && endpointData.jwtPublicName.length < 3)
        || (endpointData.jwtPublicName && endpointData.jwtPublicName.length > 20)
      ) {
        setSecurityErr(
          'JWT public key name should have a length between 3 and 20.',
        );
        return false;
      }

      if (
        !('jwtPublicKey' in endpointData)
        || endpointData.jwtPublicKey === ''
      ) {
        highlight('jwt-key', false);
        setSecurityErr('Please fill JWT public key field.');
        return false;
      }
      if (
        (endpointData.jwtPublicKey && endpointData.jwtPublicKey.length < 170)
        || (endpointData.jwtPublicKey && endpointData.jwtPublicKey.length > 800)
      ) {
        highlight('jwt-key', false);
        setSecurityErr(
          'JWT public key should have a length between 170 and 800.',
        );
        return false;
      }
    } else {
      return true;
    }
    removeHighlight('jwt-key-name', true);
    removeHighlight('jwt-key', true);
    return true;
  };

  const saveEndpointData = async () => {
    if (validateSecurityDetails()) {
      setLoaderShow(true);
      setModalTitle('Status');
      setBtnText('Ok');

      let payload;
      if (endpointData.securityEnabled && endpointData.jwtEnabled) {
        payload = {
          id: endpointId,
          securityEnabled: endpointData.securityEnabled
            ? endpointData.securityEnabled
            : false,
          jwtEnabled: endpointData.jwtEnabled ? endpointData.jwtEnabled : false,
          jwtName: endpointData.jwtPublicName ? endpointData.jwtPublicName : '',
          jwtKey: endpointData.jwtPublicKey ? endpointData.jwtPublicKey : '',
        };
      } else {
        payload = {
          id: endpointId,
          securityEnabled: endpointData.securityEnabled
            ? endpointData.securityEnabled
            : false,
        };
      }
      const response: any = await updateSecurity(payload, 'security');
      setLoaderShow(false);
      setModalShow(true);
      setIsClose(false);
      if (response) {
        if (response && response.success) {
          setMessage(response.data.message);
          setTempEndpointData(endpointData);
        } else {
          setMessage(response.message);
        }
      } else {
        setMessage('Server Error...');
      }
    }
  };

  const copyText = (val: string) => {
    const divId: HTMLElement | null = document.getElementById(val);
    if (divId) {
      const txt = divId.textContent;
      if (txt) {
        navigator.clipboard.writeText(txt);
        setIsCopiedTxt(true);
        setTimeout(() => {
          setIsCopiedTxt(false);
        }, 1500);
      }
    }
  };

  const removeHighlight = (x: string, flag: boolean) => {
    const b = document.getElementById(x);
    if (b) {
      if (flag) {
        b.style.borderColor = 'grey';
      }
    }
  };

  const highlight = (x: string, flag: boolean) => {
    const b = document.getElementById(x);
    if (b) {
      if (flag) {
        if (!b.classList.contains('formEmptyText')) {
          b.classList.add('formEmptyText');
        }
      } else {
        b.style.borderColor = 'red';
      }
    }
  };

  // API call for get endpoint details data
  const endpointDetails = async () => {
    if (id) {
      setEndpointId(id);
      setLoaderShow(true);
      const response: any = await getEndpointDetailsData(id);
      setLoaderShow(false);
      if (response && response.success) {
        getSubsComAndQuota(response.data.subscription_id);
        setDefaultName(response.data.name);
        setEndpointName(response.data.name);
        const endpoint = {
          id: response.data.id,
          serviceId: response.data.service_id,
          endpointName: response.data.name,
          userId: response.data.user_id,
          workspaceId: response.data.workspace_id,
          apiKey: response.data.api_key,
          networkType: response.data.networkType,
          securityEnabled: response.data.security
            ? response.data.security.enabled
            : false,
          jwtEnabled: response.data.security.jwt
            ? response.data.security.jwt.enabled
            : false,
          jwtPublicName: response.data.security.jwt
            ? response.data.security.jwt.name
            : '',
          jwtPublicKey: response.data.security.jwt
            ? response.data.security.jwt.public_key
            : '',
          plan: response.data.plan ? response.data.plan : '',
          addOns: response.data.addOns ? response.data.addOns : [],
        };
        setEndpointData(endpoint);
        setTempEndpointData(endpoint);
      }
    }
  };

  const getSubsComAndQuota = async (subscriptionId: string) => {
    if (subscriptionId) {
      const response: any = await getSubsComQuotaData(subscriptionId);
      if (response && response.success) {
        setConsumption(response.data.consumption.apiUnits);
        setQuota(response.data.quota.apiUnits);
      }
    } else {
      // eslint-disable-next-line no-console
      console.log('subscriptionId is missing.');
    }
  };

  useEffect(() => {
    endpointDetails();
  }, []);

  useEffect(() => {
    validateSecurityDetails();
  }, [endpointData.jwtPublicName, endpointData.jwtPublicKey]);

  const onchangeHandler = (value: string, name: string) => {
    switch (name) {
      case 'name':
        if (value.length < 5) {
          setValidationMsg(
            'Length of the endpoint name should not be less than 5.',
          );
          highlight('endpoint-name', false);
          setEndpointData({ ...endpointData, endpointName: value });
          break;
        }
        if (value.length > 50) {
          setValidationMsg(
            'Length of the endpoint name should not be more than 50.',
          );
          highlight('endpoint-name', false);
          setEndpointData({ ...endpointData, endpointName: value });
          break;
        }
        if (!/[a-z]/i.test(value.charAt(0))) {
          highlight('endpoint-name', false);
          setValidationMsg('Endpoint name should start with an alphabet.');
          setEndpointData({ ...endpointData, endpointName: value });
          break;
        }
        if (!/^[A-Za-z0-9]*$/.test(value.charAt(value.length - 1))) {
          highlight('endpoint-name', false);
          setValidationMsg(
            'Endpoint name should end with an alphabet or a number.',
          );
          setEndpointData({ ...endpointData, endpointName: value });
          break;
        }
        if (!/^([A-Za-z0-9\s-])*$/.test(value)) {
          highlight('endpoint-name', false);
          setValidationMsg(
            'Endpoint name can only contain alphabet, number, space and hyphen.',
          );
          setEndpointData({ ...endpointData, endpointName: value });
          break;
        } else {
          setEndpointData({ ...endpointData, endpointName: value.trim() });
          setValidationMsg('');
          removeHighlight('endpoint-name', true);
        }
        setEndpointData({ ...endpointData, endpointName: value });
        break;
      case 'JwtKeyName':
        if (value === '') {
          setSecurityErr('JWT public key name should not be null');
          highlight('jwt-key-name', false);
        }
        if (value.length < 3 || value.length > 20) {
          setSecurityErr(
            'JWT public key name should have a length between 3 and 20.',
          );
          highlight('jwt-key-name', false);
        } else {
          setSecurityErr('');
          removeHighlight('jwt-key-name', true);
        }
        setEndpointData({ ...endpointData, jwtPublicName: value });
        break;
      case 'JwtKey':
        if (value === '') {
          setSecurityErr('Please fill JWT public key field.');
          highlight('jwt-key', false);
        }
        if (value.length < 170 || value.length > 800) {
          setSecurityErr(
            'JWT public key should have a length between 170 and 800.',
          );
          highlight('jwt-key', false);
          return false;
        }
        setSecurityErr('');
        removeHighlight('jwt-key', true);
        setEndpointData({ ...endpointData, jwtPublicKey: value });
        break;
      default:
        break;
    }
    return null;
  };
  const checkBoxHandler = (op: string) => {
    let value: boolean;
    if (op === 'security') {
      value = endpointData.securityEnabled
        ? endpointData.securityEnabled
        : false;
      if (value) {
        setEndpointData({ ...endpointData, securityEnabled: false });
      } else {
        setEndpointData({ ...endpointData, securityEnabled: true });
      }
    }
    if (op === 'jwt_enable') {
      value = endpointData.jwtEnabled ? endpointData.jwtEnabled : false;
      if (value) {
        setEndpointData({ ...endpointData, jwtEnabled: false });
      } else {
        setEndpointData({ ...endpointData, jwtEnabled: true });
      }
      setSecurityErr('');
    }
  };

  const cancelEndpointNameChange = () => {
    setEndpointData((prev) => ({
      ...prev,
      endpointName: tempEndpointData.endpointName,
    }));

    setValidationMsg('');
    removeHighlight('endpoint-name', true);

    setIsDisabled((prev) => ({ ...prev, epName: true }));
  };

  const cancelSecurityChange = () => {
    setEndpointData((prev) => ({
      ...prev,
      securityEnabled: tempEndpointData.securityEnabled,
      jwtEnabled: tempEndpointData.jwtEnabled,
      jwtPublicKey: tempEndpointData.jwtPublicKey,
      jwtPublicName: tempEndpointData.jwtPublicName,
    }));

    setSecurityErr('');
    removeHighlight('jwt-key-name', true);
    removeHighlight('jwt-key', true);
    setIsDisabled((prev) => ({ ...prev, epSecurity: true }));
  };

  const onOKClick = () => {
    setMessage('');
    setModalTitle('');
    setModalShow(false);
    setIsClose(false);
    window.location.reload();
  };

  return (
    <>
      <div className="mt-5 col-11 mx-auto">
        <>
          <div className="my-4 editinput" id="editinput">
            <div className="row mb-3">
              <div className="col-sm-6 col-md-6 col-xl-6 col-lg-6 mx-auto">
                <label
                  htmlFor="endpoint-name"
                  className="col-auto col-form-label fw-bold"
                  style={{ fontSize: '16px' }}
                >
                  Name
                  <span className="text-danger">*</span>
                </label>
                <div className="d-flex">
                  <Inputbox
                    type="text"
                    className="form-control"
                    id="endpoint-name"
                    name="name"
                    defaultValue={endpointData.endpointName}
                    disabled={isDisabled.epName}
                    onChange={(e: { target: { value: string } }) => onchangeHandler(e.target.value, 'name')}
                    autocomplete="off"
                  />
                  &nbsp;&nbsp;&nbsp;&nbsp;
                  {isDisabled.epName && (
                    <Button
                      type="button"
                      className="btn btn-primary rounded "
                      onClick={() => setIsDisabled((prev) => ({ ...prev, epName: false }))}
                    >
                      Edit
                    </Button>
                  )}
                  {!isDisabled.epName && (
                    <Button
                      type="button"
                      className="btn btn-secondary rounded"
                      onClick={cancelEndpointNameChange}
                    >
                      Cancel
                    </Button>
                  )}
                  &nbsp;&nbsp;&nbsp;
                  {!isDisabled.epName && (
                    <Button
                      type="button"
                      className="col-auto btn btn-success rounded"
                      onClick={saveName}
                      style={{ height: 'fit-content' }}
                    >
                      Save
                    </Button>
                  )}
                </div>
              </div>
              <div className="col-sm-6 col-md-6 col-xl-6 col-lg-6 text-end pt-4">
                <div className="row">
                  <div
                    className="col-8 fw-bold text-end"
                    style={{ fontSize: '16px' }}
                  >
                    Network Type :-
                  </div>
                  <div className="col-4 text-start">
                    {endpointData.networkType}
                  </div>
                </div>
                <div className="row">
                  <div className="col-8 fw-bold text-end">Plan :-</div>
                  <div className="col-4 text-start">
                    {endpointData.plan ? endpointData.plan : ''}
                  </div>
                </div>
                <div className="row">
                  <div className="col-8 fw-bold text-end">Quota :-</div>
                  <div className="col-4 text-start">{`${quota} API Units`}</div>
                </div>
                <div className="row">
                  <div className="col-8 fw-bold text-end">Consumption :-</div>
                  <div className="col-4 text-start">
                    {`${consumption} API Units`}
                  </div>
                </div>
              </div>
            </div>
            {validationMsg ? (
              <div>
                <p className="text-danger text-center">{validationMsg}</p>
              </div>
            ) : null}
          </div>
          {isCopiedTxt && (
            <div
              className="alert alert-success text-center mt-3 mb-3"
              role="alert"
            >
              Copied
            </div>
          )}
          <div className="row">
            <div className="col-xl-6 col-lg-6 col-md-12 col-sm-12 mt-4">
              <div className="card py-4 px-4 border shadow-sm h-100 endpointcard">
                <p className="text-center fw-bold" style={{ fontSize: '16px' }}>
                  Endpoint
                </p>
                <p className="fw-bold mt-4 mb-2 d-flex justify-content-between">
                  HTTP
{' '}
                  <i
                    aria-hidden
                    className="fa-regular fa-copy fs-6 mt-2 text-primary cursor"
                    onClick={() => copyText('http')}
                  />
                </p>
                <div className="d-flex align-items-center gap-2">
                  <div className="mb-3 col-12 border px-1 pt-3 rounded-1 bg-light">
                    <p id="http">
                      {`${Urls.getEndpointHttpUrl()}/${endpointData.apiKey}/`}
                    </p>
                  </div>
                </div>

                <p className="fw-bold mt-4 mb-2 d-flex justify-content-between">
                  WS
{' '}
                  <i
                    aria-hidden
                    className="fa-regular fa-copy fs-6 mt-2 text-primary cursor"
                    onClick={() => copyText('wss')}
                  />
                </p>
                <div className="d-flex align-items-center gap-2">
                  <div className="mb-3 col-12 border pt-3 px-1 rounded-1 bg-light">
                    <p id="wss">
                      {`${Urls.getEndpointWssUrl()}/${endpointData.apiKey}/`}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-xl-6 col-lg-6 col-md-12 col-sm-12 mt-4">
              <Snippet
                setIsCopiedTxt={setIsCopiedTxt}
                endpointData={endpointData}
              />
            </div>
          </div>
        </>
        <div className="my-5">
          <div className="d-flex align-items-center justify-content-between col-sm-6 pe-2">
            <div className="form-check form-switch">
              <label className="form-check-label fw-bold" htmlFor="security">
                SECURITY
              </label>
              <Inputbox
                className="form-check-input"
                type="checkbox"
                id="security"
                disabled={isDisabled.epSecurity}
                checked={endpointData.securityEnabled}
                name="security"
                onChange={() => checkBoxHandler('security')}
              />
            </div>
            <div>
              {isDisabled.epSecurity && (
                <Button
                  type="button"
                  className="btn btn-primary rounded me-2"
                  onClick={() => setIsDisabled((prev) => ({ ...prev, epSecurity: false }))}
                >
                  Edit
                </Button>
              )}
              {!isDisabled.epSecurity && (
                <button
                  type="button"
                  className="btn btn-secondary rounded me-2"
                  onClick={cancelSecurityChange}
                >
                  Cancel
                </button>
              )}
              {!isDisabled.epSecurity && (
                <button
                  type="button"
                  className="btn btn-success rounded me-2"
                  onClick={saveEndpointData}
                >
                  Save
                </button>
              )}
            </div>
          </div>
          <h6 className="my-4">JWT</h6>
          <div className="col-xl-4 col-lg-5 col-sm-8 col-md-8">
            <div className="form-check mb-3 editinput" id="editinput3">
              <Inputbox
                className="form-check-input"
                type="checkbox"
                name="jwt_enable"
                id="require-jwt"
                disabled={
                  !(
                    isDisabled.epSecurity === false
                    && endpointData.securityEnabled === true
                  )
                }
                checked={endpointData.jwtEnabled}
                onChange={() => checkBoxHandler('jwt_enable')}
              />
              <label className="form-check-label ms-2" htmlFor="require-jwt">
                Require JWT
              </label>
              <i
                title="Restrict API usage to requests that include a valid JWT"
                className="fa-solid fa-circle-info ms-1"
              />
            </div>
          </div>
          <div className="mb-2 row editinput" id="editinput4">
            <label
              htmlFor="jwt-key-name"
              className="col-sm-12 col-md-4 col-lg-2 col-xl-2 col-form-label col-6"
            >
              JWT Public Key Name
              <span className="text-danger">*</span>
            </label>
            <div className="col-sm-12 col-md-6 col-xl-4 col-lg-5 col-12">
              <Inputbox
                type="text"
                className="form-control"
                name="JwtKeyName"
                id="jwt-key-name"
                defaultValue={endpointData.jwtPublicName}
                disabled={
                  !(
                    isDisabled.epSecurity === false
                    && endpointData.jwtEnabled === true
                  )
                }
                onChange={(e: { target: { value: string } }) => onchangeHandler(e.target.value, 'JwtKeyName')}
                autocomplete="off"
              />
            </div>
          </div>
          <div className="mb-3 row editinput" id="editinput2">
            <label
              htmlFor="jwt-key"
              className="col-sm-12 col-md-4 col-lg-2 col-xl-2 col-form-label col-6"
            >
              JWT Public Key
              <span className="text-danger">*</span>
              <i
                title="Restrict API usage to requests that include a valid JWT. Public key can be generated using RSA and ECDSA algorithms only."
                className="fa-solid fa-circle-info ms-1"
              />
            </label>

            <div className="col-sm-12 col-md-6 col-xl-4 col-lg-5 col-12">
              <textarea
                rows={4}
                cols={50}
                className="form-control "
                name="JwtKey"
                id="jwt-key"
                placeholder={
                  endpointData.jwtEnabled === true
                    ? '-----BEGIN PUBLIC KEY----------END PUBLIC KEY-----'
                    : ''
                }
                defaultValue={endpointData.jwtPublicKey}
                disabled={
                  !(
                    isDisabled.epSecurity === false
                    && endpointData.jwtEnabled === true
                  )
                }
                onChange={(e) => onchangeHandler(e.target.value, 'JwtKey')}
                autoComplete="off"
              />
            </div>
          </div>
          {securityErr ? (
            <div className="text-center">
              <p className="text-danger py-2">{securityErr}</p>
            </div>
          ) : null}
        </div>
      </div>
      {loaderShow && <Loading />}
      <Modal
        show={modalShow}
        onHide={() => {
          setModalShow(false);
          setIsClose(false);
          setModalTitle('');
          setMessage('');
        }}
        title={modalTitle}
        visibleFooter
        visibleFooterCloseButton={isClose}
        visibleHeaderCloseIcon={false}
        visibleConfirmBtn
        confirmBtnText={btnText}
        onConfirm={onOKClick}
        confirmBtnClassName=""
      >
        <p className="status text-center mt-3">{message}</p>
      </Modal>
    </>
  );
}

export default Basics;
